import { useEffect, useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Table } from '@factorialco/deprecated-design-system'
import {
  CreateRoleLineItemDocument,
  CreateSimpleLineItemDocument,
  DeleteLineItemDocument,
  QuotePricingLineItemFragment,
  QuotePricingPageDocument,
  UpdateRoleLineItemDocument,
  UpdateSimpleLineItemDocument,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Header from './Header'
import Body from './_Body'

interface QuotePhasesTableProps {
  currency: string
  lineItems: QuotePricingLineItemFragment[]
  isProductQuote: boolean
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  legalEntityCountry: string | undefined
  quoteId: number
}
const QuotePhasesTable = ({
  currency,
  lineItems,
  isProductQuote,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  legalEntityCountry,
  quoteId,
}: QuotePhasesTableProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [standaloneItems, setStandaloneItems] = useState<QuotePricingLineItemFragment[]>(lineItems)
  const discountPercentage = 0 // TODO: quote.discountPercentage

  useEffect(() => {
    setStandaloneItems(lineItems)
  }, [lineItems])

  // Simple line items
  const [createSimpleLineItem] = useMutation(CreateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateSimpleLineItem] = useMutation(UpdateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [deleteLineItem] = useMutation(DeleteLineItemDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  // Role line items
  const [createRoleLineItem] = useMutation(CreateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateRoleLineItem] = useMutation(UpdateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const updateSimpleLineItemRef = useRef(updateSimpleLineItem)
  useEffect(() => {
    updateSimpleLineItemRef.current = updateSimpleLineItem
  }, [updateSimpleLineItem])

  const updateRoleLineItemRef = useRef(updateRoleLineItem)
  useEffect(() => {
    updateRoleLineItemRef.current = updateRoleLineItem
  }, [updateRoleLineItem])

  const standaloneItemsRef = useRef(standaloneItems)
  useEffect(() => {
    standaloneItemsRef.current = standaloneItems
  }, [standaloneItems])

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (itemId: number, newValues: Partial<QuotePricingLineItemFragment>) => {
        const currentItem = standaloneItemsRef.current.find((item) => item.id === itemId)
        if (!currentItem) return

        if (currentItem.type === 'role') {
          await updateRoleLineItemRef.current({
            variables: {
              id: itemId,
              quantity: newValues.quantity ?? currentItem.quantity ?? 0,
              oldLevelId: newValues.oldJobCatalogLevelId ?? currentItem.oldJobCatalogLevelId,
              treeLevelId: newValues.jobCatalogTreeLevelId ?? currentItem.jobCatalogTreeLevelId,
            },
          })
        } else {
          await updateSimpleLineItemRef.current({
            variables: {
              id: itemId,
              discountPercentage,
              description: newValues.description ?? currentItem.description,
              quantity: newValues.quantity ?? currentItem.quantity ?? 0,
              unitPriceCents: newValues.unitPriceCents ?? currentItem.unitPriceCents ?? 0,
              unitCostCents: newValues.unitCostCents ?? currentItem.unitCostCents ?? 0,
              taxRateId: newValues.taxRateId ?? currentItem.taxRateId ?? undefined,
            },
          })
        }
      }, 1000),
    []
  )

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  const profitPercentage = (profitCents: number, costCents: number) => {
    if (profitCents === 0) {
      return 0
    }

    if (costCents === 0) {
      if (profitCents > 0) {
        return 100
      }
      return -100
    }
    return (profitCents / costCents) * 100
  }

  const updateQuoteApolloCachedData = (items: QuotePricingLineItemFragment[]) => {
    const baseAmountCents = items.reduce((sum, item) => sum + (item.basePriceCents || 0), 0)
    const taxesTotalAmountCents = items.reduce(
      (sum, item) =>
        sum +
        ((item.basePriceCents || 0) - (item.basePriceCents || 0) * discountPercentage) *
          (item.taxRatePercentage || 0),
      0
    )
    const totalAmountCents = baseAmountCents + taxesTotalAmountCents
    const baseCostCents = items.reduce((sum, item) => sum + (item.baseCostCents || 0), 0)
    const totalHours = items
      .filter((item) => item.quantityType === 'hours')
      .reduce((sum, item) => sum + (item.quantity || 0), 0)
    const totalProfitCents = totalAmountCents - baseCostCents
    const totalProfitPercentage = profitPercentage(totalProfitCents, baseCostCents)

    const cachedData = apolloClient.readQuery({
      query: QuotePricingPageDocument,
      variables: { id: quoteId, companyId: Number(companyId) },
    })

    if (cachedData?.finance?.quote) {
      apolloClient.writeQuery({
        query: QuotePricingPageDocument,
        variables: { id: quoteId, companyId: Number(companyId) },
        data: {
          ...cachedData,
          finance: {
            ...cachedData.finance,
            quote: {
              ...cachedData.finance.quote,
              baseAmountCents,
              totalAmountCents,
              baseCostCents,
              totalHours,
              taxesTotalAmountCents,
              totalProfitCents,
              totalProfitPercentage,
            },
          },
        },
      })
    }
  }

  const revertOptimisticResponse = (itemId: number, mutationType: 'create' | 'delete') => {
    if (mutationType === 'create') {
      setStandaloneItems((prev) => prev.filter((item) => item.id !== itemId))
    } else if (mutationType === 'delete') {
      setStandaloneItems(standaloneItems)
    }
    updateQuoteApolloCachedData(standaloneItems)
    app.setFlash({
      type: 'wrong',
      message: i18n.t('common.save_error'),
      time: 2000,
    })
  }

  const handleUpdateStandaloneItem = async (
    itemId: number,
    newValues: Partial<QuotePricingLineItemFragment>
  ) => {
    const updatedItems = standaloneItems.map((item) =>
      item.id === itemId ? { ...item, ...newValues } : item
    )
    // handling state before mutation response
    setStandaloneItems(updatedItems)
    updateQuoteApolloCachedData(updatedItems)
    debouncedUpdate(itemId, newValues)
  }

  const handleAddStandaloneItem = async (itemType: 'role' | 'simple') => {
    const tempId = Date.now() * -1
    const optimisticItem: QuotePricingLineItemFragment = {
      id: tempId,
      description: '-',
      quantity: 1,
      unitPriceCents: 0,
      baseCostCents: 0,
      basePriceCents: 0,
      unitCostCents: 0,
      taxRatePercentage: 0,
      baseProfitPercentage: 0,
      parentId: null,
      quantityType: 'units' as const,
      taxRateId: null,
      type: itemType,
    }

    // handling an optimistic response
    setStandaloneItems((prev) => [...prev, optimisticItem])

    if (itemType === 'simple') {
      const result = await createSimpleLineItem({
        variables: {
          description: '-',
          discountPercentage,
          quantity: 1,
          sourceId: quoteId,
          sourceType: 'quote',
          unitPriceCents: 0,
          unitCostCents: 0,
        },
      })
      const handled = handleResult(result, () => undefined)
      if (!handled.success) {
        revertOptimisticResponse(tempId, 'create')
      }
    } else {
      const result = await createRoleLineItem({
        variables: {
          quantity: 1,
          sourceId: quoteId,
          sourceType: 'quote',
        },
      })
      const handled = handleResult(result, () => undefined)
      if (!handled.success) {
        revertOptimisticResponse(tempId, 'create')
      }
    }
  }

  const handleDeleteStandaloneItem = async (itemId: number) => {
    const updatedItems = standaloneItems.filter((item) => item.id !== itemId)
    setStandaloneItems(updatedItems)
    updateQuoteApolloCachedData(updatedItems)

    const result = await deleteLineItem({
      variables: {
        id: itemId,
      },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.deleted_successfully'),
        time: 2000,
      })
    } else {
      revertOptimisticResponse(itemId, 'delete')
    }
  }

  return (
    <Table autoWidth>
      <Header isDraftStatus={isDraftStatus} isProductQuote={isProductQuote} />
      <Body
        standaloneItems={standaloneItems}
        currency={currency}
        isDraftStatus={isDraftStatus}
        isProductQuote={isProductQuote}
        isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
        onUpdateStandaloneItem={handleUpdateStandaloneItem}
        onAddStandaloneItem={handleAddStandaloneItem}
        onDeleteStandaloneItem={handleDeleteStandaloneItem}
        legalEntityCountry={legalEntityCountry}
      />
    </Table>
  )
}

export default QuotePhasesTable
